/**
 * V2 admin · Translations management.
 *
 * Lists every UI translation key with EN/DE side-by-side inputs. Editors
 * type into either column; on blur, the row is upserted to public.translations.
 * Search filters by key (and EN/DE values) client-side; namespace dropdown
 * narrows to a single dot-segment family ("property.*", "auth.*", etc.).
 *
 * Why a flat side-by-side editor instead of a per-row tabbed BilingualTextarea:
 * UI strings are mostly short labels ("Save", "Cancel", "Available from")
 * and editors want to see EN+DE at the same glance — tabbing would slow
 * the workflow on a 300-row list. The bilingual tabbed editor is for
 * paragraph-length DB content like property descriptions, where one
 * language at a time is plenty of screen.
 */
import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Languages,
  Plus,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DebouncedSearchInput } from "@/components/admin/v2/primitives/DebouncedSearchInput";
import { EmptyState } from "@/components/admin/v2/primitives/EmptyState";
import { TableSkeleton } from "@/components/admin/v2/primitives/TableSkeleton";
import { AddTranslationDialog } from "@/components/admin/v2/translations/AddTranslationDialog";
import { TranslationsRow } from "@/components/admin/v2/translations/TranslationsRow";
import {
  useTranslations,
  useUpsertTranslation,
  type TranslationRow,
} from "@/hooks/v2/useTranslations";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";

const ALL_NAMESPACES = "__all__";

// Page-size options. 50 is the default — comfortable on a 1080p screen
// without forcing a long scroll, and keeps the DOM cheap (each row has
// two controlled inputs). Bumping to 200 for editors on large monitors
// who prefer "see everything at once" for a single namespace.
const PAGE_SIZES = [25, 50, 100, 200] as const;
const DEFAULT_PAGE_SIZE = 50;

export default function V2TranslationsPage() {
  const { isOwnerPortalOnly } = useAuth();
  const { t } = useLanguage();
  const [search, setSearch] = useState("");
  const [namespace, setNamespace] = useState<string>(ALL_NAMESPACES);
  const [missingOnly, setMissingOnly] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const { toast } = useToast();

  const { data: rows, isLoading, error } = useTranslations();
  const upsertMut = useUpsertTranslation();

  // Distinct namespaces for the dropdown. Computed from current rows.
  const namespaces = useMemo(() => {
    if (!rows) return [] as string[];
    const set = new Set<string>();
    for (const r of rows) set.add(r.namespace);
    return Array.from(set).sort();
  }, [rows]);

  // Counts for the header summary. Useful at a glance:
  //   "240 keys · 12 missing DE · 3 missing EN"
  const stats = useMemo(() => {
    const total = rows?.length ?? 0;
    let missingDe = 0;
    let missingEn = 0;
    for (const r of rows ?? []) {
      if (!r.en || !r.en.trim()) missingEn += 1;
      if (!r.de || !r.de.trim()) missingDe += 1;
    }
    return { total, missingDe, missingEn };
  }, [rows]);

  const filtered = useMemo(() => {
    if (!rows) return [] as TranslationRow[];
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (namespace !== ALL_NAMESPACES && r.namespace !== namespace)
        return false;
      if (missingOnly) {
        const hasEn = !!(r.en && r.en.trim());
        const hasDe = !!(r.de && r.de.trim());
        if (hasEn && hasDe) return false;
      }
      if (!q) return true;
      // Match against key OR either translation — editors searching for
      // "WLAN" should find the property.amenity.wifi row.
      return (
        r.key.toLowerCase().includes(q) ||
        (r.en ?? "").toLowerCase().includes(q) ||
        (r.de ?? "").toLowerCase().includes(q)
      );
    });
  }, [rows, search, namespace, missingOnly]);

  // Pagination math. Page is 1-based for human-friendly display ("Page 2 of 6")
  // but slice math is 0-based, hence the -1 in startIdx. totalPages floors to
  // 1 even when filtered is empty so we never end up showing "Page 1 of 0".
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  // Clamp page in case filters shrank the list out from under the cursor
  // before the reset effect has a chance to run.
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const paginated = useMemo(
    () => filtered.slice(startIdx, startIdx + pageSize),
    [filtered, startIdx, pageSize]
  );

  // Reset to page 1 whenever the underlying filter set changes — otherwise
  // a search that narrows results from 261 → 12 leaves the user stranded
  // on page 4 of 1.
  useEffect(() => {
    setPage(1);
  }, [search, namespace, missingOnly, pageSize]);

  if (isOwnerPortalOnly) {
    return <Navigate to="/admin/properties" replace />;
  }

  async function handleSave(
    row: TranslationRow,
    next: { en: string; de: string }
  ) {
    // Skip the round-trip if nothing actually changed (blur fires on every
    // tab-out, even when the user just glanced at the field).
    if ((row.en ?? "") === next.en && (row.de ?? "") === next.de) return;
    try {
      await upsertMut.mutateAsync({
        key: row.key,
        en: next.en,
        de: next.de,
        description: row.description,
      });
    } catch (err) {
      toast({
        title: t(
          "admin.translations.couldNotSaveTranslation",
          "Could not save translation"
        ),
        description:
          err instanceof Error
            ? err.message
            : t("admin.error.unknownError", "Unknown error"),
        variant: "destructive",
      });
    }
  }

  return (
    <AdminV2Layout
      title={t("admin.nav.translations", "Translations")}
      breadcrumbs={[
        { label: t("admin.nav.label", "Admin"), to: "/admin" },
        { label: t("admin.nav.translations", "Translations") },
      ]}
      actions={
        <Button onClick={() => setAddOpen(true)} size="sm">
          <Plus className="h-4 w-4 mr-1.5" />
          {t("admin.action.addKey", "Add key")}
        </Button>
      }
    >
      <Card className="p-4 mb-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <DebouncedSearchInput
            value={search}
            onDebouncedChange={setSearch}
            placeholder={t(
              "admin.translations.searchPlaceholder",
              "Search key, EN, or DE…"
            )}
            className="flex-1"
          />
          <Select value={namespace} onValueChange={setNamespace}>
            <SelectTrigger className="sm:w-56">
              <SelectValue
                placeholder={t(
                  "admin.translations.allNamespaces",
                  "All namespaces"
                )}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_NAMESPACES}>
                {t("admin.translations.allNamespaces", "All namespaces")}
              </SelectItem>
              {namespaces.map((ns) => (
                <SelectItem key={ns} value={ns}>
                  {ns}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant={missingOnly ? "default" : "outline"}
            size="sm"
            onClick={() => setMissingOnly((v) => !v)}
            title={t(
              "admin.translations.missingOnlyHint",
              "Show only keys missing one or both languages"
            )}
          >
            <AlertTriangle className="h-4 w-4 mr-1.5" />
            {t("admin.translations.missingOnly", "Missing only")}
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
          <Badge variant="secondary">
            {stats.total} {t("admin.translations.keysCount", "keys")}
          </Badge>
          {stats.missingDe > 0 && (
            <Badge
              variant="outline"
              className="text-amber-700 border-amber-300"
            >
              {stats.missingDe}{" "}
              {t("admin.translations.missingDe", "missing DE")}
            </Badge>
          )}
          {stats.missingEn > 0 && (
            <Badge
              variant="outline"
              className="text-amber-700 border-amber-300"
            >
              {stats.missingEn}{" "}
              {t("admin.translations.missingEn", "missing EN")}
            </Badge>
          )}
          {filtered.length !== stats.total && (
            <Badge variant="outline">
              {filtered.length}{" "}
              {t("admin.translations.matchFilter", "match filter")}
            </Badge>
          )}
        </div>
      </Card>

      {error ? (
        <Card className="p-6">
          <p className="text-sm text-destructive">
            {t(
              "admin.translations.couldNotLoad",
              "Could not load translations"
            )}
            {": "}
            {error.message}
          </p>
        </Card>
      ) : isLoading ? (
        <Card className="p-2">
          <TableSkeleton
            columns={[
              { label: t("admin.translations.colKey", "Key"), bar: "w-48" },
              { label: "EN", bar: "w-40" },
              { label: "DE", bar: "w-40" },
            ]}
            rows={8}
          />
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-2">
          <EmptyState
            icon={Languages}
            title={
              search || namespace !== ALL_NAMESPACES || missingOnly
                ? t(
                    "admin.translations.noKeysMatch",
                    "No keys match your filters"
                  )
                : t(
                    "admin.translations.noTranslationsTitle",
                    "No translations yet"
                  )
            }
            description={
              search || namespace !== ALL_NAMESPACES || missingOnly
                ? t(
                    "admin.translations.noKeysMatchDesc",
                    "Try clearing the search or namespace filter."
                  )
                : t(
                    "admin.translations.noTranslationsDesc",
                    'Click "Add key" to create the first translation, or run the seed migration to import existing strings.'
                  )
            }
          />
        </Card>
      ) : (
        <Card className="overflow-hidden">
          {/* Pagination caps the rendered rows at pageSize (default 50), so
              no virtualization needed even when the underlying set grows. */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2 font-medium w-1/4">
                    {t("admin.translations.colKey", "Key")}
                  </th>
                  <th className="text-left px-4 py-2 font-medium">EN</th>
                  <th className="text-left px-4 py-2 font-medium">DE</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((row) => (
                  <TranslationsRow
                    key={row.key}
                    row={row}
                    onSave={(next) => handleSave(row, next)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination footer. Stays inside the Card so it visually anchors
              to the table, with a top border so it reads as a footer rather
              than a separate strip. Only shown when there's something to
              paginate (filtered.length > smallest page-size option) — under
              that, the controls would be cosmetic noise. */}
          {filtered.length > PAGE_SIZES[0] && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 px-4 py-3 border-t bg-muted/20 text-xs">
              <div className="text-muted-foreground">
                {t("admin.translations.showing", "Showing")}{" "}
                <span className="font-medium text-foreground">
                  {filtered.length === 0 ? 0 : startIdx + 1}–
                  {Math.min(startIdx + pageSize, filtered.length)}
                </span>{" "}
                {t("admin.translations.of", "of")}{" "}
                <span className="font-medium text-foreground">
                  {filtered.length}
                </span>{" "}
                {filtered.length !== stats.total
                  ? `(${t("admin.translations.filteredFrom", "filtered from")} ${stats.total})`
                  : t("admin.translations.keysCount", "keys")}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <span className="hidden sm:inline">
                    {t("admin.translations.perPage", "Per page")}
                  </span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(v) => setPageSize(Number(v))}
                  >
                    <SelectTrigger className="h-7 px-2 text-xs w-[64px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZES.map((s) => (
                        <SelectItem
                          key={s}
                          value={String(s)}
                          className="text-xs"
                        >
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    aria-label={t(
                      "admin.translations.previousPage",
                      "Previous page"
                    )}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="px-2 text-muted-foreground tabular-nums">
                    {t("admin.translations.page", "Page")}{" "}
                    <span className="font-medium text-foreground">
                      {safePage}
                    </span>{" "}
                    {t("admin.translations.of", "of")}{" "}
                    <span className="font-medium text-foreground">
                      {totalPages}
                    </span>
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    aria-label={t("admin.translations.nextPage", "Next page")}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      <AddTranslationDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        existingKeys={rows?.map((r) => r.key) ?? []}
        onCreated={() => {
          toast({ title: t("admin.translations.keyAdded", "Key added") });
          setAddOpen(false);
        }}
      />
    </AdminV2Layout>
  );
}
